import { useReducer } from 'react';
import type { MouseEvent, FormEvent } from 'react';
import { action } from '@storybook/addon-actions';
import type { Meta, StoryFn } from '@storybook/react';

import { Button, createUID, FieldGroupList, Form, Text } from '@pega/cosmos-react-core';

import {
  DemoFieldRenderer,
  demoGroupMeta,
  ExpensesFieldRenderer,
  expensesGroupMeta,
  fieldGroupReducer,
  InsuranceFieldRenderer,
  insuranceGroupMeta
} from './FieldGroupList.mocks';

export default {
  title: 'Core/FieldGroupList',
  component: FieldGroupList,
  args: {
    allowAdd: true,
    allowDelete: true,
    itemLevelDelete: false,
    contextualLabel: 'record'
  },
  argTypes: {
    allowAdd: { control: { type: 'boolean' } },
    allowDelete: { control: { type: 'boolean' } },
    itemLevelDelete: { control: { type: 'boolean' } },
    contextualLabel: { control: { type: 'text' } }
  }
} as Meta;

interface FieldGroupListDemoProps {
  allowAdd?: boolean;
  allowDelete?: boolean;
  itemLevelDelete?: boolean;
  contextualLabel?: string;
}

export const ExpensesDemo: StoryFn<FieldGroupListDemoProps> = (args: FieldGroupListDemoProps) => {
  const [state, dispatch] = useReducer(
    fieldGroupReducer(expensesGroupMeta, ExpensesFieldRenderer),
    [
      {
        name: `${expensesGroupMeta.label} 1`,
        id: createUID(),
        headingTag: 'h3',
        children: expensesGroupMeta.fields.map(ExpensesFieldRenderer)
      }
    ]
  );

  return (
    <Form
      actions={
        <>
          <Button
            name='Cancel'
            variant='secondary'
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              action('Click')(`${e.currentTarget.name}:${e.type}`);
            }}
          >
            Cancel
          </Button>
          <Button name='Submit' type='submit' variant='primary'>
            Submit
          </Button>
        </>
      }
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        action('Submit')(`Form:${e.type}`);
      }}
      style={{ margin: 'auto', maxWidth: '37.5rem' }}
    >
      <Text variant='h2'>Expense report</Text>
      <FieldGroupList
        items={
          args.itemLevelDelete
            ? state.map((item, i) => ({
                ...item,
                onDelete:
                  args.allowDelete && i !== 0
                    ? id => {
                        dispatch({ action: 'delete', id });
                      }
                    : undefined
              }))
            : state
        }
        onAdd={
          args.allowAdd
            ? () => {
                dispatch({ action: 'add' });
              }
            : undefined
        }
        onDelete={
          args.allowDelete && !args.itemLevelDelete
            ? id => {
                dispatch({ action: 'delete', id });
              }
            : undefined
        }
        contextualLabel={args.contextualLabel}
      />
    </Form>
  );
};

export const InsuranceDemo: StoryFn<FieldGroupListDemoProps> = (args: FieldGroupListDemoProps) => {
  const [state, dispatch] = useReducer(
    fieldGroupReducer(insuranceGroupMeta, InsuranceFieldRenderer),
    [
      {
        name: `${insuranceGroupMeta.label} 1`,
        id: createUID(),
        headingTag: 'h3',
        children: insuranceGroupMeta.fields.map(InsuranceFieldRenderer)
      }
    ]
  );

  return (
    <Form
      actions={
        <>
          <Button
            name='Cancel'
            variant='secondary'
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              action('Click')(`${e.currentTarget.name}:${e.type}`);
            }}
          >
            Cancel
          </Button>
          <Button name='Submit' type='submit' variant='primary'>
            Submit
          </Button>
        </>
      }
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        action('Submit')(`Form:${e.type}`);
      }}
      style={{ margin: 'auto', maxWidth: '37.5rem' }}
    >
      <Text variant='h1'>Insurance step 1</Text>
      <Text variant='h2'>Dependants</Text>
      <FieldGroupList
        items={
          args.itemLevelDelete
            ? state.map((item, i) => ({
                ...item,
                onDelete:
                  args.allowDelete && i !== 0
                    ? id => {
                        dispatch({ action: 'delete', id });
                      }
                    : undefined
              }))
            : state
        }
        onAdd={
          args.allowAdd
            ? () => {
                dispatch({ action: 'add' });
              }
            : undefined
        }
        onDelete={
          args.allowDelete && !args.itemLevelDelete
            ? id => {
                dispatch({ action: 'delete', id });
              }
            : undefined
        }
        contextualLabel={args.contextualLabel}
      />
    </Form>
  );
};

export const FieldGroupListDemo: StoryFn<FieldGroupListDemoProps> = (
  args: FieldGroupListDemoProps
) => {
  const [state, dispatch] = useReducer(fieldGroupReducer(demoGroupMeta, DemoFieldRenderer), [
    {
      name: `${demoGroupMeta.label} 1`,
      id: createUID(),
      headingTag: 'h3',
      children: demoGroupMeta.fields.map(DemoFieldRenderer)
    }
  ]);

  return (
    <FieldGroupList
      items={
        args.itemLevelDelete
          ? state.map((item, i) => ({
              ...item,
              onDelete:
                args.allowDelete && i !== 0
                  ? id => {
                      dispatch({ action: 'delete', id });
                    }
                  : undefined
            }))
          : state
      }
      onAdd={
        args.allowAdd
          ? () => {
              dispatch({ action: 'add' });
            }
          : undefined
      }
      onDelete={
        args.allowDelete && !args.itemLevelDelete
          ? id => {
              dispatch({ action: 'delete', id });
            }
          : undefined
      }
      contextualLabel={args.contextualLabel}
    />
  );
};
